<template>
  <div role="main" class="container resultados-cap">
    <div class="resultados-head">
      <div>
        <p class="resultados-kicker">Encuestas de capacitacion</p>
        <h2 class="font-weight-bold mb-0 titulo-cap">Mis resultados</h2>
      </div>
      <button class="btn btn-outline-primary btn-sm" type="button" @click="cargarResultados">
        <i class="fas fa-sync-alt mr-1"></i>
        Actualizar
      </button>
    </div>

    <div v-if="loading" class="text-center p-4">
      <div class="spinner-border" role="status"></div>
      <div class="mt-2">Cargando resultados...</div>
    </div>

    <div v-else-if="error" class="alert alert-danger">{{ error }}</div>

    <template v-else>
      <section class="summary-grid">
        <article class="summary-card">
          <span class="summary-label">Promedio general</span>
          <strong>{{ promedioGeneral }}</strong>
          <small>de 5 puntos</small>
        </article>
        <article class="summary-card">
          <span class="summary-label">Encuestas recibidas</span>
          <strong>{{ resumen.totalEncuestas || 0 }}</strong>
          <small>respuestas registradas</small>
        </article>
        <article class="summary-card summary-card--rank">
          <span class="summary-label">Ranking actual</span>
          <strong>{{ rankingActual }}</strong>
          <small>{{ totalRankingLabel }}</small>
        </article>
      </section>

      <div v-if="sinEncuestas" class="alert alert-info">
        Todavia no hay encuestas registradas para mostrar resultados.
      </div>

      <section class="result-section">
        <div class="section-title-row">
          <h3>Ranking</h3>
        </div>

        <div v-if="ranking.length === 0" class="empty-box">
          No hay respuestas registradas en el ranking.
        </div>

        <div v-else class="ranking-list">
          <article
            v-for="item in ranking"
            :key="item.capacitador_id"
            :class="['ranking-row', { 'ranking-row--me': esMiRegistro(item) }]"
          >
            <div class="rank-place">#{{ item.lugar }}</div>
            <div class="rank-person">
              <strong>{{ item.nombre }}</strong>
              <small v-if="esMiRegistro(item)">Tu posicion actual</small>
            </div>
            <div class="rank-score">
              <strong>{{ formatDecimal(item.promedio_general) }}</strong>
              <small>{{ item.total_encuestas }} enc.</small>
            </div>
          </article>
        </div>
      </section>

      <section class="result-grid">
        <article class="result-panel">
          <div class="section-title-row">
            <h3>Fortalezas</h3>
            <span>Mejores metricas</span>
          </div>
          <div v-if="fortalezas.length === 0" class="empty-box">
            Sin datos suficientes.
          </div>
          <template v-else>
            <div
              v-for="metrica in fortalezas"
              :key="`fortaleza-${metrica.key}`"
              class="metric-row"
            >
              <div class="metric-row__meta">
                <span>{{ metrica.label }}</span>
                <strong>{{ formatDecimal(metrica.promedio) }}</strong>
              </div>
              <div class="metric-row__track">
                <div class="metric-row__fill" :style="{ width: metricPercentage(metrica.promedio) + '%' }"></div>
              </div>
            </div>
          </template>
        </article>

        <article class="result-panel">
          <div class="section-title-row">
            <h3>Oportunidades</h3>
            <span>Menor promedio</span>
          </div>
          <div v-if="oportunidades.length === 0" class="empty-box">
            Sin datos suficientes.
          </div>
          <template v-else>
            <div
              v-for="metrica in oportunidades"
              :key="`oportunidad-${metrica.key}`"
              class="metric-row"
            >
              <div class="metric-row__meta">
                <span>{{ metrica.label }}</span>
                <strong>{{ formatDecimal(metrica.promedio) }}</strong>
              </div>
              <div class="metric-row__track">
                <div class="metric-row__fill" :style="{ width: metricPercentage(metrica.promedio) + '%' }"></div>
              </div>
            </div>
          </template>
        </article>
      </section>

      <section class="result-section">
        <div class="section-title-row">
          <h3>Comentarios sobre oportunidades de mejora</h3>
          <span>Pregunta: ¿Que podriamos mejorar?</span>
        </div>

        <div v-if="comentarios.length === 0" class="empty-box">
          No hay comentarios recientes.
        </div>

        <div v-else class="comments-list">
          <article v-for="comentario in comentarios" :key="comentario.id" class="comment-item">
            <p>{{ comentario.texto }}</p>
            <small>{{ formatDate(comentario.fecha) }}</small>
          </article>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import axiosInstance, { getAccessToken, setAccessToken } from "../../services/axiosInstance";

export default {
  name: "ResultadosEncuestasCapacitador",
  data() {
    return {
      loading: false,
      error: "",
      resumen: {},
      ranking: [],
      fortalezas: [],
      oportunidades: [],
      comentarios: [],
    };
  },
  computed: {
    sinEncuestas() {
      return Number(this.resumen.totalEncuestas || 0) === 0;
    },
    promedioGeneral() {
      return this.formatDecimal(this.resumen.promedioGeneral || 0);
    },
    rankingActual() {
      if (!this.resumen.rankingActual) return "S/R";
      return `#${this.resumen.rankingActual}`;
    },
    totalRankingLabel() {
      const total = Number(this.resumen.totalRanking || 0);
      if (!this.resumen.rankingActual || total === 0) return "sin ranking";
      return `de ${total} capacitadores`;
    },
  },
  methods: {
    async cargarResultados() {
      if (Number(localStorage.getItem("role")) !== 2) {
        this.$router.replace("/home");
        return;
      }

      this.loading = true;
      this.error = "";
      try {
        if (!getAccessToken()) {
          const { data: authData } = await axiosInstance.get("/users/auth/refresh", {
            __skip401Handler: true,
          });
          setAccessToken(authData.accessToken);
        }

        const { data } = await axiosInstance.get("/cap/encuestas/resultados");
        this.resumen = data.miResumen || {};
        this.ranking = data.ranking || [];
        this.fortalezas = data.fortalezas || [];
        this.oportunidades = data.oportunidades || [];
        this.comentarios = data.comentarios || [];
      } catch (error) {
        console.error("Error al obtener resultados de encuestas:", error);
        this.error = "No fue posible cargar los resultados.";
      } finally {
        this.loading = false;
      }
    },
    esMiRegistro(item) {
      return Number(item.lugar) === Number(this.resumen.rankingActual);
    },
    formatDecimal(value) {
      return Number(value || 0).toFixed(1);
    },
    metricPercentage(value) {
      return Math.max(0, Math.min(100, (Number(value || 0) / 5) * 100));
    },
    formatDate(value) {
      if (!value) return "";
      return new Date(value).toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
  },
  mounted() {
    this.cargarResultados();
  },
};
</script>

<style scoped>
.resultados-cap {
  max-width: 980px;
  padding-bottom: 26px;
}

.resultados-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.resultados-kicker {
  color: #637083;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
  margin-bottom: 3px;
  text-transform: uppercase;
}

.summary-grid {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-bottom: 14px;
}

.summary-card,
.result-panel,
.result-section {
  background: #ffffff;
  border: 1px solid #e3e9f0;
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(15, 76, 150, 0.06);
  min-width: 0;
}

.summary-card {
  min-height: 106px;
  padding: 14px;
}

.summary-card strong {
  color: #0f4c96;
  display: block;
  font-size: 30px;
  line-height: 1.1;
  margin-top: 7px;
}

.summary-card small,
.summary-label {
  color: #68778a;
  font-size: 12px;
}

.summary-label {
  display: block;
  font-weight: 700;
}

.summary-card--rank strong {
  color: #0b7b65;
}

.result-section,
.result-panel {
  padding: 14px;
  margin-bottom: 14px;
}

.result-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.section-title-row {
  align-items: flex-start;
  display: flex;
  gap: 8px;
  justify-content: space-between;
  margin-bottom: 12px;
}

.section-title-row h3 {
  color: #172033;
  font-size: 17px;
  font-weight: 700;
  margin: 0;
}

.section-title-row span {
  color: #758397;
  font-size: 12px;
  text-align: right;
}

.ranking-list,
.comments-list {
  display: grid;
  gap: 8px;
  min-width: 0;
}

.ranking-row {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e8eef5;
  border-radius: 8px;
  display: grid;
  gap: 10px;
  grid-template-columns: 44px minmax(0, 1fr) 72px;
  min-height: 62px;
  padding: 10px;
}

.ranking-row--me {
  background: #edf7f4;
  border-color: #9fd6c8;
}

.rank-place {
  align-items: center;
  background: #0f4c96;
  border-radius: 8px;
  color: #ffffff;
  display: flex;
  font-weight: 800;
  height: 38px;
  justify-content: center;
}

.ranking-row--me .rank-place {
  background: #0b7b65;
}

.rank-person,
.rank-score {
  min-width: 0;
}

.rank-person strong {
  color: #172033;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rank-person small,
.rank-score small {
  color: #69798e;
  display: block;
  font-size: 12px;
}

.rank-score {
  text-align: right;
}

.rank-score strong {
  color: #0f4c96;
  display: block;
  font-size: 20px;
}

.metric-row {
  margin-bottom: 14px;
}

.metric-row:last-child {
  margin-bottom: 0;
}

.metric-row__meta {
  align-items: flex-start;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  margin-bottom: 7px;
}

.metric-row__meta span {
  color: #233044;
  font-size: 13px;
  line-height: 1.25;
}

.metric-row__meta strong {
  color: #0f4c96;
  font-size: 15px;
}

.metric-row__track {
  background: #e6edf5;
  border-radius: 999px;
  height: 8px;
  overflow: hidden;
}

.metric-row__fill {
  background: linear-gradient(to right, #0f4c96, #0b7b65);
  border-radius: inherit;
  height: 100%;
}

.comment-item {
  background: #f8fafc;
  border: 1px solid #e8eef5;
  border-radius: 8px;
  min-width: 0;
  overflow: hidden;
  padding: 11px 12px;
}

.comment-item p {
  color: #253348;
  line-height: 1.35;
  margin-bottom: 6px;
  max-width: 100%;
  overflow-wrap: anywhere;
  text-align: left;
  white-space: normal;
  word-break: break-word;
}

.comment-item small,
.empty-box {
  color: #758397;
  font-size: 13px;
}

.empty-box {
  background: #f8fafc;
  border: 1px dashed #cbd6e2;
  border-radius: 8px;
  padding: 12px;
}

@media (max-width: 767.98px) {
  .resultados-cap {
    padding-left: 0;
    padding-right: 0;
  }

  .resultados-head {
    align-items: center;
  }

  .summary-grid,
  .result-grid {
    grid-template-columns: 1fr;
  }

  .summary-card {
    min-height: auto;
  }

  .summary-card strong {
    font-size: 28px;
  }

  .section-title-row {
    display: block;
  }

  .section-title-row span {
    display: block;
    margin-top: 3px;
    text-align: left;
  }
}
</style>
